// ============================================================================
// Canonical cut-sheet record route contract
// ----------------------------------------------------------------------------
// `/processing/cut-sheet/<recordId>` is the ONE shape the cut-sheet editor
// claims. It is a splat row with a single mounted component, which is the
// `weighinsessions` arrangement rather than the `tasks` one: there is no hub
// beneath the prefix to be swallowed by a record matcher, because a cut-sheet
// editor with no Processing record behind it has nothing to edit.
//
// WHY A SEPARATE MODULE, for one predicate. The page decides what to mount from
// the raw pathname, and a matcher that lives inside the component drifts as soon
// as the component grows a second branch. Keeping the shape here means the route
// contract can be read, and reviewed, without reading the editor.
//
// WHY THE SHAPE IS THIS NARROW. The record id is a Processing record id and goes
// straight into an RPC argument. Accepting `..`, a separator, an embedded NUL or
// a line break as "an opaque id" means the editor asks the server about
// something the router never intended to name. Every one of those is refused
// twice: once on the raw segment and once on the decoded one, so an encoding the
// router does not itself decode cannot smuggle a rejected character through.
//
// RESERVED SEGMENTS. `src/lib/routes.ts` registers no `/processing/<segment>`
// path, alias source, alias target or prefix alias, and `/processing` itself is
// an exact row that this prefix cannot reach. Nothing is reserved beneath
// `/processing/cut-sheet/` and nothing may become reserved without changing this
// comment and the predicate together.
// ============================================================================

export const CUT_SHEET_EDITOR_PATHNAME_PREFIX = '/processing/cut-sheet/';

/** The route row's path. Exported so the table and this contract cannot drift. */
export const CUT_SHEET_EDITOR_ROUTE_PATH = '/processing/cut-sheet/*';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator either way, no NUL, no line break,
// and no query or fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules after ONE decode, which is what turns `%2F`, `%5C`, `%00`,
// `%0A`, `%0D`, `%2E` and `%2E%2E` into rejections rather than accepted opaque
// text. A malformed escape makes decodeURIComponent throw, and the catch below
// turns that into a refusal too.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/**
 * The Processing record id this pathname names, or null when the pathname is
 * not a canonical cut-sheet editor target.
 *
 * Returns the DECODED id, because that is what the RPC needs. A caller that
 * wants only the yes/no should use `isCanonicalCutSheetEditorPathname`, which is
 * this function with the answer narrowed — having one implementation means the
 * predicate and the extractor can never disagree about a given URL.
 */
export function cutSheetEditorRecordId(pathname: unknown): string | null {
  if (typeof pathname !== 'string' || !pathname.startsWith(CUT_SHEET_EDITOR_PATHNAME_PREFIX)) return null;
  const rawSegment = pathname.slice(CUT_SHEET_EDITOR_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return null;
  try {
    const segment = decodeURIComponent(rawSegment);
    return isSafeDecodedSegment(segment) ? segment : null;
  } catch {
    return null;
  }
}

/** `/processing/cut-sheet/<id>` with exactly one decoded-safe segment. */
export function isCanonicalCutSheetEditorPathname(pathname: unknown): pathname is string {
  return cutSheetEditorRecordId(pathname) !== null;
}

// ============================================================================
// Carried URL state (Constitution N06/N14, Table B) — and what stays out.
// ----------------------------------------------------------------------------
// Exactly two UI facts are addressable, and each has ONE canonical spelling:
//
//   ?tab=diagram   the Diagram reference tab is open (N06/N14 tab-in-URL;
//                  the shared useUrlTab/searchWithTab pattern writes it and
//                  DELETES the param at the default, so `?tab=sheet` is not a
//                  supported spelling — the default state's address is bare)
//   #usual         the "Save The Usual" defaults sub-screen is open
//                  (Constitution Table B route row)
//
// They combine: `?tab=diagram#usual` is canonical — the Usual screen keeps the
// tab it was opened from, so closing it returns there.
//
// STILL DELIBERATELY NOT IN THE URL: the questionnaire's own state — which
// chapter is open, which pool is focused, any draft answer. A half-answered
// questionnaire is not a shareable address, and putting it in the URL would
// invite a link that reopens someone else's unreviewed draft at a section
// that no longer exists. Nothing outside the two spellings above is carried:
// extra parameters, duplicate parameters, other values and other fragments
// are refused here rather than normalized away, so a URL this editor claims
// is always exactly the address its state came from.
// ============================================================================

/** The only query parameter the editor carries, and its only non-default value. */
export const CUT_SHEET_TAB_SEARCH = '?tab=diagram';
/** The only fragment the editor carries. */
export const CUT_SHEET_USUAL_HASH = '#usual';

function isCanonicalCutSheetEditorSearch(search: unknown): boolean {
  // Raw comparison, deliberately. A parse-based check would accept spellings
  // that are not the canonical one: URLSearchParams decodes percent-escapes
  // (`?tab=%64iagram`, `?%74ab=diagram` parse to the canonical entry) and
  // trims `&` edges (`?tab=diagram&`, `?&tab=diagram` parse to one entry).
  // The carried state has exactly one address per state, so anything but the
  // canonical spelling refuses and falls through exactly as it did before
  // this contract carried state.
  if (search === undefined || search === '') return true;
  return search === CUT_SHEET_TAB_SEARCH;
}

function isCanonicalCutSheetEditorHash(hash: unknown): boolean {
  return hash === undefined || hash === '' || hash === CUT_SHEET_USUAL_HASH;
}

/**
 * The full location form. React Router supplies pathname, search and hash
 * separately; a bare string is accepted only when it is plainly a pathname, so
 * neither a caller nor a test seam can smuggle an unsupported query or fragment
 * into the claimed target.
 *
 * The carried state is exactly `?tab=diagram` and/or `#usual` (see the block
 * comment above); every other search or hash refuses, so the released surface
 * keeps stable ownership of every state it supports and unsupported input
 * falls through exactly as it did before this contract carried state.
 */
export function isCanonicalCutSheetEditorLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalCutSheetEditorPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  const {pathname, search, hash} = location as LocationLike;
  return (
    isCanonicalCutSheetEditorSearch(search) &&
    isCanonicalCutSheetEditorHash(hash) &&
    isCanonicalCutSheetEditorPathname(pathname)
  );
}

/** The editor's address for one Processing record. Encoded once, here. */
export function cutSheetEditorPath(recordId: string): string {
  return `${CUT_SHEET_EDITOR_PATHNAME_PREFIX}${encodeURIComponent(recordId)}`;
}
